/* Source-compatible funnel events and atomic last-touch cart attribution.
 * No trackers, network requests, native-wallet interception, or purchase events.
 */
(() => {
	if (window.YPAnalytics) return;
	const keys = ['fbclid', 'ttclid'];
	const storageKey = 'yp_attr_last_touch_v1';
	const ttl = 7 * 24 * 60 * 60 * 1000;
	const isId = value => typeof value === 'string' && value.length > 0 && value.length <= 512;
	const variants = {
		'48930707603683': ['BASIC', '1M', 'AE'],
		'48930687418595': ['BASIC', '12M', 'AE'],
		'48930680635619': ['BASIC', '6M', 'AE'],
		'48930714616035': ['PREMIUM', '1M', 'AE'],
		'48930702426339': ['PREMIUM', '12M', 'AE'],
		'48930700525795': ['PREMIUM', '6M', 'AE'],
		'49485116211427': ['STANDARD', '1M', 'KW'],
		'49485138854115': ['STANDARD', '12M', 'KW'],
		'49485150814435': ['STANDARD', '1M', 'QA'],
		'49485158482147': ['STANDARD', '12M', 'QA'],
	};
	const clearLegacy = storage => keys.forEach(key => storage.removeItem('yp_attr_' + key));
	const marketingAllowed = () => {
		try {
			return window.Shopify?.customerPrivacy?.marketingAllowed() === true;
		} catch {
			return false;
		}
	};
	const clearStoredClickIds = () => {
		try {
			const storage = window.sessionStorage;
			clearLegacy(storage);
			storage.removeItem(storageKey);
		} catch { /* Storage unavailable: there is nothing else to clear. */ }
	};
	const captureClickIds = () => {
		if (!marketingAllowed()) return;
		const params = new URLSearchParams(window.location.search);
		const ids = {};
		keys.forEach(key => { const value = params.get(key)?.trim(); if (isId(value)) ids[key] = value; });
		if (!Object.keys(ids).length) return;
		try {
			const storage = window.sessionStorage;
			clearLegacy(storage);
			storage.setItem(storageKey, JSON.stringify({capturedAt: Date.now(), ids}));
		} catch { /* Storage unavailable: preserve the purchase flow without attribution. */ }
	};
	const getCartAttributes = () => {
		try {
			const storage = window.sessionStorage;
			clearLegacy(storage);
			if (!marketingAllowed()) {
				storage.removeItem(storageKey);
				return [];
			}
			const raw = storage.getItem(storageKey);
			if (!raw) return [];
			let touch;
			try { touch = JSON.parse(raw); } catch { storage.removeItem(storageKey); return []; }
			const now = Date.now();
			if (!touch || typeof touch.capturedAt !== 'number' || !Number.isFinite(touch.capturedAt)
				|| touch.capturedAt > now || now - touch.capturedAt > ttl || !touch.ids || typeof touch.ids !== 'object') {
				storage.removeItem(storageKey); return [];
			}
			const attributes = keys.filter(key => isId(touch.ids[key])).map(key => ({key, value: touch.ids[key]}));
			if (!attributes.length) storage.removeItem(storageKey);
			return attributes;
		} catch { return []; }
	};
	const approvedCheckout = input => {
		try {
			const url = new URL(input, window.location.origin);
			const match = url.pathname.match(/^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?cart\/(\d+):1$/);
			if (url.origin !== window.location.origin || url.username || url.password || !match
				|| !Object.hasOwn(variants, match[1])) return null;
			return {url, variant: variants[match[1]]};
		} catch { return null; }
	};
	const checkoutSku = input => {
		const approved = approvedCheckout(input);
		if (!approved) return null;
		const [tier, duration, country] = approved.variant;
		return {tier, duration, country};
	};
	const decorateCheckoutURL = input => {
		const approved = approvedCheckout(input);
		if (!approved) return input;
		// A cart permalink already opens checkout. Serializing the historical bare
		// ?checkout as checkout= makes Shopify parse a String where it expects a
		// Hash and reject the request. Omit only this scalar flag; keep nested
		// checkout[...] parameters and all purchase attributes intact.
		approved.url.searchParams.delete('checkout');
		// Replace both keys, never retain an id from a different touch or stale URL.
		keys.forEach(key => approved.url.searchParams.delete(`attributes[${key}]`));
		getCartAttributes().forEach(({key, value}) => approved.url.searchParams.set(`attributes[${key}]`, value));
		return approved.url.href;
	};
	const locale = () => {
		if (document.documentElement.lang.toLowerCase().startsWith('ar')) return 'ar';
		try { return window.localStorage.getItem('v3-lang') === 'ar' ? 'ar' : 'en'; } catch { return 'en'; }
	};
	const pageSurface = () => {
		const explicit = document.querySelector('[data-v3]')?.dataset.ypPage
			|| document.querySelector('[data-yp-page]')?.dataset.ypPage;
		if (explicit) return explicit;
		const view = new URLSearchParams(window.location.search).get('view') || '';
		if (/^yp-express(?:-(?:kw|qa))?(?:-ar)?$/.test(view)) return 'tiers';
		if (/^yp-home(?:-(?:kw|qa))?(?:-ar)?$/.test(view)) return 'home';
		if (/^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?(?:pages\/)?plans\/?$/.test(window.location.pathname)) return 'plans';
		return window.location.pathname === '/' ? 'home' : 'unknown';
	};
	const pageMarket = () => {
		const explicit = document.querySelector('[data-v3]')?.dataset.ypMarket;
		if (['AE', 'KW', 'QA'].includes(explicit)) return explicit;
		const params = new URLSearchParams(window.location.search);
		const viewMarket = params.get('view')?.match(/^yp-(?:express|home)-(kw|qa)(?:-ar)?$/)?.[1]?.toUpperCase();
		const queryMarket = params.get('market')?.toUpperCase();
		return ['KW', 'QA'].includes(viewMarket) ? viewMarket : ['AE', 'KW', 'QA'].includes(queryMarket) ? queryMarket : 'AE';
	};
	const push = payload => {
		window.dataLayer = window.dataLayer ?? [];
		window.dataLayer.push({...payload, locale: locale(), market: pageMarket(), page_path: window.location.pathname, page_surface: pageSurface()});
	};
	const skuEvent = (prefix, sku, extra = {}) => {
		if (!sku || !Object.values(variants).some(([tier, duration, country]) => tier === sku.tier && duration === sku.duration && country === sku.country)) return;
		push({event: `${prefix}_${sku.tier}_${sku.duration}_${sku.country}`, tier: sku.tier, duration: sku.duration, country: sku.country, ...extra});
	};
	window.YPAnalytics = Object.freeze({
		captureClickIds, getCartAttributes, decorateCheckoutURL, checkoutSku,
		trackButtonClick: label => push({event: 'CLICK_BUTTON', label}),
		trackMarketChange: (from, to) => { if (from !== to) push({event: 'MARKET_CHANGE', market_from: from, market_to: to}); },
		trackDialogOpen: sku => skuEvent('OPEN_DIALOG', sku),
		trackCheckoutStart: sku => skuEvent('CHECKOUT_START', sku),
		trackCheckoutError: sku => skuEvent('CHECKOUT_ERROR', sku, {error_reason: 'unknown'}),
	});
	const syncClickIdsWithConsent = () => {
		if (marketingAllowed()) captureClickIds();
		else clearStoredClickIds();
	};
	syncClickIdsWithConsent();
	document.addEventListener?.('visitorConsentCollected', syncClickIdsWithConsent);
})();
